import React from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import ValidationElement from '../ValidationElement';
import Generic from '../Generic';

export default class NumberComponent extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      max: props.max,
      min: props.min,
    };

    this.handleError = this.handleError.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.numberComponent = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.max !== this.props.max
      || nextProps.min !== this.props.min
      || !isEqual(nextProps.value, this.props.value)
      || nextProps.name !== this.props.name
    ) {
      const oldmax = this.state.max;
      const oldmin = this.state.min;
      this.setState({ max: nextProps.max, min: nextProps.min, value: nextProps.value }, () => {
        if (oldmax !== nextProps.max || oldmin !== nextProps.min) {
          this.handleValidation(
            {
              fake: true,
              target: {
                name: this.props.name,
              },
            },
            null
          );
        }
      });
    }
  }

  /**
   * Prevent non-numerical values from being entered
   *
   * @param {string} value
   */
  sanitizedInput = (value) => {
    // TODO: eslint claims exec is faster than match. We'll need to investigate
    // and refactor.
    switch (this.props.type) {
      case 'decimal':
        // eslint-disable-next-line
        if (!value.match(/^(\s*|\d+|\d+.\d+)$/)) {
          return value.replace(/[^.\d]+/, '');
        }
        break;

      case 'whole':
        // eslint-disable-next-line
        if (!value.match(/^(\s*|\d+)$/)) {
          return value.replace(/\D/g, '');
        }

        break;
      default:
        break;
    }

    return value;
  };

  /**
   * This will block certain key presses from happening, which results in no
   * characters appearing.
   */
  onKeyPress = (event) => {
    const allowedKeys = [
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Backspace',
      'Control',
      'Alt',
      'Shift',
    ];

    if (this.props.type === 'decimal') { allowedKeys.push('.'); }

    if ((event.key >= '0' && event.key <= '9') || allowedKeys.includes(event.key)) {
      // The expectation with props.onKeyPress is to be used with testing.
      return this.props.onKeyPress?.(event);
    }
    event.preventDefault();
    return this.props.onKeyPress?.(event);
  };

  /**
   * Handle the change event.
   */
  handleChange(event) {
    super.handleChange(event);
  }

  handleBlur(event) {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        name: this.props.name,
        value: this.sanitizedInput(event.target.value),
      });
    }

    super.handleBlur(event);
  }

  handleError(value, arr) {
    const errors = arr.concat(
      this.constructor.errors.map((err) => ({
        code: !this.props.prefix ? err.code : `${this.props.prefix}.${err.code}`,
        valid: err.func(value, this.props),
        uid: this.state.uid,
      }))
    );

    // Take the original and concatenate our new error values to it
    return this.props.onError(value, errors);
  }

  render() {
    const speech = this.props.ariaLabel ? this.props.ariaLabel : this.props.label;

    return (
      <Generic
        attribute={this.props.attribute}
        attributeList={this.props.attributeList}
        name={this.props.name}
        label={this.props.label}
        ariaLabel={speech}
        placeholder={this.props.placeholder}
        type="text"
        className={this.props.className}
        disabled={this.props.disabled}
        minlength={this.props.minlength}
        maxlength={this.props.maxlength}
        pattern={this.props.pattern}
        readonly={this.props.readonly}
        required={this.props.required}
        value={this.props.value}
        onChange={this.handleChange} // eslint-disable-line @typescript-eslint/unbound-method
        onFocus={this.props.onFocus}
        onBlur={this.props.onBlur || this.handleBlur} // eslint-disable-line @typescript-eslint/unbound-method
        onKeyDown={this.props.onKeyDown}
        onKeyPress={this.onKeyPress}
        onCopy={this.props.onCopy}
        onCut={this.props.onCut}
        onPaste={this.props.onPaste}
        clipboard={this.props.clipboard}
        tabBack={this.props.tabBack}
        tabNext={this.props.tabNext}
        ref={this.numberComponent}
        onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
        valid={this.props.valid}
        forceValidation={this.props.forceValidation}
        makeStateFromErrors={this.props.makeStateFromErrors}
      />
    );
  }
}

NumberComponent.propTypes = {
  type: PropTypes.string,
  onKeyPress: PropTypes.func,
  name: PropTypes.string,
  prefix: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  min: PropTypes.string,
  max: PropTypes.string,
  minlength: PropTypes.string,
  maxlength: PropTypes.string,
  pattern: PropTypes.string,
  readonly: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onError: PropTypes.func,
  onUpdate: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onCopy: PropTypes.func,
  onCut: PropTypes.func,
  onPaste: PropTypes.func,
  clipboard: PropTypes.string,
  tabBack: PropTypes.func,
  tabNext: PropTypes.func,
  ariaLabel: PropTypes.string,
  errorCode: PropTypes.any,
  forceValidation: PropTypes.bool,
  makeStateFromErrors: PropTypes.func,
};

NumberComponent.defaultProps = {
  type: 'whole',
  name: 'number',
  ariaLabel: '',
  label: '',
  disabled: false,
  value: '',
  max: '',
  min: '',
  prefix: '',
  pattern: '^(\\s*|\\d+)$',
  errorCode: null,
  onError: (value, arr) => arr,
  forceValidation: false,
  makeStateFromErrors: undefined,
};

NumberComponent.errors = [
  {
    code: 'min',
    func: (value, props) => {
      if (!value.length) {
        return null;
      }

      if (props.min) {
        return parseInt(value, 10) >= parseInt(props.min, 10);
      }

      return true;
    },
  },
  {
    code: 'max',
    func: (value, props) => {
      if (!value.length) {
        return null;
      }

      if (props.max) {
        return parseInt(value, 10) <= parseInt(props.max, 10);
      }

      return true;
    },
  },
];
